// A year's page with its sidebar (sidebar.json; YearSidebar/): the year page (Year/About.vue)
// and, from 2026-10-28 (Seán), its Masterclass page (Year/Masterclass.vue). Beside the page only
// where there's room for the right column (useDisplayWidth: "unlimited"); narrower, one column, the
// sidebar's control in the title's place and a panel in the page's.
import {computed, onActivated, onDeactivated, onUnmounted, watch} from "vue";
import {useDisplayWidth} from "@/composables/useDisplayWidth";
import useYearSidebar from "@/composables/useYearSidebar";

// one column, below the sidebar's width: the whole page narrower, for lines of a reading length
// (Seán, 2026-10-22: 97 characters at 740 px); global.css body.isReadingWidth. Only while the
// page shows (kept alive: off when left)
function useReadingWidth(isSingleColumn) {
  const setReadingWidth = (isOn) => document.body.classList.toggle("isReadingWidth", isOn);
  watch(isSingleColumn, setReadingWidth, {immediate: true});
  onActivated(() => setReadingWidth(isSingleColumn.value));
  onDeactivated(() => setReadingWidth(false));
  onUnmounted(() => setReadingWidth(false));
}

// sidebar: the year's sidebar.json, read by the page (require isn't defined in a module like
// this one), or null
// a page's view of the year's sidebar.json (its PAGES, e.g. "masterclass": the session whose
// highlights it shows, its panels' names), or the content as it is; null without a sidebar
export function getPageSidebar(sidebar, page) {
  const view = sidebar?.PAGES?.[page];
  if (!sidebar || !view) return sidebar;
  const isShown = ({session}) => !view.highlightSession || session === view.highlightSession;
  const shown = sidebar.HIGHLIGHTS.filter(isShown);
  const HIGHLIGHTS = view.isSessionShown === false
    ? shown.map((highlight) => ({...highlight, session: ""})) : shown;
  return {...sidebar, HIGHLIGHTS, LABELS: view.LABELS, NARROW_LABELS: view.NARROW_LABELS};
}

export function useYearPageSidebar(sidebar) {
  const width = useDisplayWidth();
  const isNarrow = computed(() => width.value !== "unlimited");
  const {panel} = useYearSidebar();
  const isPanelShown = computed(() => Boolean(sidebar && isNarrow.value && panel.value));
  const isSingleColumn = computed(() => Boolean(sidebar && isNarrow.value));
  useReadingWidth(isSingleColumn);
  // the narrow control may have panels of its own (sidebar.json PAGES NARROW_LABELS: the
  // Masterclass page's Program)
  const narrowSidebar = sidebar?.NARROW_LABELS ? {...sidebar, LABELS: sidebar.NARROW_LABELS} : sidebar;
  return {sidebar, narrowSidebar, isNarrow, isPanelShown, isSingleColumn};
}
